import * as pdfjs from 'pdfjs-dist'
import trabajador from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

/**
 * Leer el RUT.
 *
 * El RUT que expide la DIAN es un PDF con texto —no una imagen escaneada—,
 * así que se puede leer sin OCR y sin mandarlo a ningún lado: TODO PASA EN EL
 * NAVEGADOR. El documento tributario del cliente no sale del computador de
 * quien lo carga.
 *
 * Lo que se saca son las casillas del formulario 001:
 *
 *   5  NIT            26 Número de identificación
 *   31 Primer apellido 32 Segundo apellido
 *   33 Primer nombre   34 Otros nombres
 *   35 Razón social
 *   40 Ciudad/Municipio  41 Dirección principal
 *   42 Correo electrónico  44 Teléfono 1
 *
 * Lo leído NO SE GUARDA SOLO: se muestra para que alguien lo confirme. Un PDF
 * mal leído que entra derecho a la contabilidad es peor que teclear a mano.
 */

pdfjs.GlobalWorkerOptions.workerSrc = trabajador

export type DatosDelRut = {
  identificacion: string | null
  /** El dígito de verificación, cuando el RUT lo trae aparte. */
  dv: string | null
  razonSocial: string | null
  primerApellido: string | null
  segundoApellido: string | null
  primerNombre: string | null
  otrosNombres: string | null
  direccion: string | null
  ciudad: string | null
  correo: string | null
  telefono: string | null
  /** Verdadero cuando el RUT trae razón social: es empresa, no persona. */
  esEmpresa: boolean
  /** Lo que no se pudo leer, para decirlo en vez de callarlo. */
  faltantes: string[]
}

/** Saca todo el texto del PDF, página por página. */
async function textoDelPdf(archivo: File): Promise<string> {
  const datos = await archivo.arrayBuffer()
  const documento = await pdfjs.getDocument({ data: datos }).promise

  const paginas: string[] = []

  for (let i = 1; i <= documento.numPages; i++) {
    const pagina = await documento.getPage(i)
    const contenido = await pagina.getTextContent()

    // Se unen con salto de línea y no con espacio: en el RUT cada casilla es
    // su propio fragmento, y pegarlos todos seguidos hace imposible saber
    // dónde termina una y empieza la otra.
    paginas.push(
      contenido.items
        .map((item) => ('str' in item ? item.str : ''))
        .join('\n'),
    )
  }

  // Se suelta el documento: sin esto, cargar varios RUT seguidos deja en
  // memoria el contenido de todos.
  await documento.cleanup()

  return paginas.join('\n')
}

/**
 * Sin tildes y en minúscula, solo para comparar rótulos.
 *
 * El texto que sale de un PDF puede traer las tildes o no, según cómo se haya
 * generado el documento: la misma casilla aparece como «Razón social» o
 * «Razon social». Buscando con tildes se falla en la mitad de los RUT, y esa
 * mitad falla en silencio, que es lo peor que puede pasar aquí.
 */
function plano(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

/**
 * El valor que sigue a una casilla.
 *
 * El RUT imprime el número de casilla, su rótulo y después el dato. Se busca
 * el rótulo y se toman las líneas siguientes hasta la próxima casilla, porque
 * el dato puede venir partido en varios fragmentos.
 *
 * El rótulo se busca sin tildes; el valor se devuelve tal como vino.
 */
function casilla(lineas: string[], rotulo: RegExp): string | null {
  const i = lineas.findIndex((linea) => rotulo.test(plano(linea)))
  if (i === -1) return null

  const recogido: string[] = []

  for (let j = i + 1; j < Math.min(i + 6, lineas.length); j++) {
    const linea = lineas[j].trim()

    // Se paró en el rótulo de otra casilla: lo que sigue ya no es este dato.
    if (/^\d{1,3}\.?\s*[A-ZÁÉÍÓÚÑ]/.test(linea) && linea.length > 6) break
    if (!linea) continue

    recogido.push(linea)

    // Con una línea basta salvo que sea muy corta, que suele ser un pedazo.
    if (recogido.join(' ').length > 3) break
  }

  const valor = recogido.join(' ').trim()
  return valor.length > 0 ? valor : null
}

function limpiar(valor: string | null): string | null {
  if (!valor) return null
  const limpio = valor.replace(/\s+/g, ' ').trim()
  return limpio.length > 0 ? limpio : null
}

export async function leerRut(archivo: File): Promise<DatosDelRut> {
  const texto = await textoDelPdf(archivo)
  const lineas = texto.split('\n').map((l) => l.trim())

  const identificacion = limpiar(
    casilla(lineas, /numero de identificacion tributaria|^5\.?\s*nit|identificacion tributaria/),
  )?.replace(/\D/g, '') ?? null

  const razonSocial = limpiar(casilla(lineas, /razon social/))
  const primerApellido = limpiar(casilla(lineas, /primer apellido/))
  const segundoApellido = limpiar(casilla(lineas, /segundo apellido/))
  const primerNombre = limpiar(casilla(lineas, /primer nombre/))
  const otrosNombres = limpiar(casilla(lineas, /otros nombres/))
  const direccion = limpiar(casilla(lineas, /direccion principal/))
  const ciudad = limpiar(casilla(lineas, /ciudad\s*\/\s*municipio/))
  const correo = limpiar(casilla(lineas, /correo electronico/))
  const telefono = limpiar(casilla(lineas, /telefono\s*1|telefono/))?.replace(/\D/g, '') ?? null
  const dv = limpiar(casilla(lineas, /^6\.?\s*dv$|^dv$/))?.replace(/\D/g, '') ?? null

  const esEmpresa = Boolean(razonSocial && razonSocial.length > 2)

  const faltantes: string[] = []
  if (!identificacion) faltantes.push('el NIT o la identificación')
  if (!razonSocial && !primerNombre) faltantes.push('el nombre')

  return {
    identificacion,
    dv,
    razonSocial,
    primerApellido,
    segundoApellido,
    primerNombre,
    otrosNombres,
    direccion,
    ciudad,
    correo,
    telefono,
    esEmpresa,
    faltantes,
  }
}

/** El nombre y los apellidos como los quiere World Office. */
export function nombreYApellidos(datos: DatosDelRut): { nombre: string; apellidos: string } {
  if (datos.esEmpresa) {
    // Una empresa lleva todo en el nombre: World Office no le pone apellidos.
    return { nombre: datos.razonSocial ?? '', apellidos: '' }
  }

  const nombre = [datos.primerNombre, datos.otrosNombres].filter(Boolean).join(' ')
  const apellidos = [datos.primerApellido, datos.segundoApellido].filter(Boolean).join(' ')

  return { nombre, apellidos }
}
